import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Spinner, Empty } from "@/components/UIKit";
import { toast } from "sonner";
import { fmtDateTime, fmtNumber } from "@/lib/format";
import {
  Building2, Plus, Search, Users as UsersIcon, FileSignature, Gauge, Receipt,
  ShieldAlert, CheckCircle2, XCircle, Loader2, RefreshCw, X, Sparkles,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function SuperadminTenants() {
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [seedBusy, setSeedBusy] = useState(false);

  const seedDemo = async () => {
    if (!window.confirm("Fyll R-Strøm med 45 demokunder + 148 fakturaer? Sletter eksisterende R-Strøm-data først.")) return;
    setSeedBusy(true);
    try {
      const { data } = await api.post("/superadmin/seed-rstrom-demo");
      toast.success(`Demodata seedet: ${data.seeded.customers} kunder, ${data.seeded.invoices} fakturaer, ${data.seeded.cases} saker`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Seed feilet"); }
    finally { setSeedBusy(false); }
  };

  const load = async () => {
    setItems(null);
    const [{ data }, { data: s }] = await Promise.all([
      api.get("/superadmin/tenants"),
      api.get("/superadmin/stats"),
    ]);
    setItems(data.items || []);
    setStats(s);
  };
  useEffect(() => { load(); }, []);

  const filtered = (items || []).filter((t) =>
    !q || t.name?.toLowerCase().includes(q.toLowerCase()) || t.orgnr?.includes(q)
  );

  const toggleActive = async (t) => {
    setBusy(true);
    try {
      await api.post(`/superadmin/tenants/${t.id}/${t.is_active ? "deactivate" : "activate"}`);
      toast.success(t.is_active ? "Tenant deaktivert" : "Tenant aktivert");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Feilet"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="superadmin-tenants-page">
      <PageHeader
        title="Kraftselskap-tenants"
        subtitle="Administrer alle kraftselskaper som bruker Krafthub-plattformen"
        actions={
          <>
            <button onClick={seedDemo} disabled={seedBusy}
              className="h-9 px-3 rounded-md border border-emerald-500/30 text-emerald-400 text-xs hover:bg-emerald-500/10 flex items-center gap-1.5 disabled:opacity-50"
              data-testid="tenants-seed-demo">
              {seedBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Seed R-Strøm demodata
            </button>
            <button onClick={load} disabled={items === null}
              className="h-9 px-3 rounded-md border border-zinc-800 text-zinc-300 text-xs flex items-center gap-1.5 hover:border-zinc-700"
              data-testid="tenants-refresh">
              <RefreshCw className="h-3.5 w-3.5" /> Oppdater
            </button>
            <button onClick={() => setOpenNew(true)}
              className="h-9 px-4 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium flex items-center gap-1.5"
              data-testid="tenants-new-btn">
              <Plus className="h-4 w-4" /> Nytt kraftselskap
            </button>
          </>
        }
      />

      {/* Plattform-stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <StatBox testid="stat-tenants" icon={Building2} label="Tenants" value={stats.tenants.total} sub={`${stats.tenants.active} aktive`} />
          <StatBox testid="stat-users" icon={UsersIcon} label="Brukere" value={fmtNumber(stats.users)} />
          <StatBox testid="stat-customers" icon={UsersIcon} label="Kunder totalt" value={fmtNumber(stats.customers)} />
          <StatBox testid="stat-invoices" icon={Receipt} label="Fakturaer" value={fmtNumber(stats.invoices)} />
          <StatBox testid="stat-mrr" icon={Sparkles} label="Plattform-MRR" value="—" sub="Stripe kommer" accent="text-zinc-500" />
        </div>
      )}

      {/* Søk */}
      <div className="relative mb-4 max-w-md">
        <Search className="h-4 w-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Søk etter navn eller orgnr…"
          data-testid="tenants-search"
          className="w-full h-10 pl-10 pr-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm focus:outline-none focus:border-emerald-500/40" />
      </div>

      {/* Liste */}
      {items === null ? (
        <div className="p-8"><Spinner label="Laster tenants…" /></div>
      ) : filtered.length === 0 ? (
        <Empty title="Ingen tenants" subtitle="Opprett første kraftselskap for å komme i gang" />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="tenants-list">
          {filtered.map((t) => (
            <Card key={t.id} className="p-5" testid={`tenant-card-${t.orgnr}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-heading text-lg font-semibold text-zinc-50 truncate">{t.name}</div>
                    {t.is_active ? (
                      <span className="text-[10px] uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-1.5 py-0.5 rounded">Aktiv</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider bg-red-500/10 text-red-400 border border-red-500/20 px-1.5 py-0.5 rounded">Deaktivert</span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5 font-mono">Orgnr: {t.orgnr} · Opprettet {fmtDateTime(t.created_at)}</div>
                </div>
                <button onClick={() => toggleActive(t)} disabled={busy}
                  data-testid={`tenant-toggle-${t.orgnr}`}
                  className={`h-8 px-3 rounded-md text-xs border ${t.is_active ? "border-red-500/30 text-red-400 hover:bg-red-500/10" : "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"} shrink-0 ml-2`}>
                  {t.is_active ? "Deaktiver" : "Aktiver"}
                </button>
              </div>

              <div className="grid grid-cols-4 gap-2 mb-3">
                <MiniStat icon={UsersIcon} label="Brukere" value={t.stats.users} />
                <MiniStat icon={UsersIcon} label="Kunder" value={t.stats.customers} />
                <MiniStat icon={FileSignature} label="Avtaler" value={t.stats.contracts} />
                <MiniStat icon={Gauge} label="Målepunkter" value={t.stats.metering_points} />
              </div>

              {t.primary_admin ? (
                <div className="border-t border-zinc-900 pt-3 text-xs text-zinc-400 flex items-center gap-2">
                  <ShieldAlert className="h-3.5 w-3.5 text-emerald-400" />
                  <span className="text-zinc-500">Admin:</span>
                  <span className="text-zinc-200 truncate">{t.primary_admin.full_name}</span>
                  <span className="text-emerald-400 font-mono">{t.primary_admin.email}</span>
                  {t.primary_admin.last_login_at && (
                    <span className="text-zinc-500 ml-auto">Sist innlogget {fmtDateTime(t.primary_admin.last_login_at)}</span>
                  )}
                </div>
              ) : (
                <div className="border-t border-zinc-900 pt-3 text-xs text-amber-400 flex items-center gap-2">
                  <XCircle className="h-3.5 w-3.5" /> Ingen admin registrert
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <NyTenantDialog open={openNew} onOpenChange={setOpenNew} onCreated={() => { setOpenNew(false); load(); }} />
    </div>
  );
}

function StatBox({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <Card className="p-4" testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">{label}</div>
        <Icon className={`h-3.5 w-3.5 ${accent || "text-emerald-400"}`} />
      </div>
      <div className="font-heading text-2xl font-medium tabular-nums text-zinc-50">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>}
    </Card>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="bg-zinc-950/50 border border-zinc-900 rounded-md p-2">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-sm font-medium text-zinc-100 tabular-nums mt-0.5">{fmtNumber(value)}</div>
    </div>
  );
}

function NyTenantDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    name: "", orgnr: "", contact_email: "", contact_phone: "",
    address: "", postal_code: "", city: "", default_price_area: "NO1",
    admin_email: "", admin_password: "", admin_full_name: "",
  });
  const [busy, setBusy] = useState(false);
  const [brregBusy, setBrregBusy] = useState(false);

  useEffect(() => {
    if (!open) setForm({
      name: "", orgnr: "", contact_email: "", contact_phone: "",
      address: "", postal_code: "", city: "", default_price_area: "NO1",
      admin_email: "", admin_password: "", admin_full_name: "",
    });
  }, [open]);

  const lookupBrreg = async () => {
    if (!/^\d{9}$/.test(form.orgnr)) {
      toast.error("Orgnr må være 9 sifre");
      return;
    }
    setBrregBusy(true);
    try {
      const { data } = await api.get(`/kraftadmin/brreg/${form.orgnr}`);
      setForm((f) => ({
        ...f,
        name: data.navn || f.name,
        address: data.forretningsadresse?.adresse?.[0] || f.address,
        postal_code: data.forretningsadresse?.postnummer || f.postal_code,
        city: data.forretningsadresse?.poststed || f.city,
      }));
      toast.success(`Hentet fra Brreg: ${data.navn}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Ikke funnet i Brreg"); }
    finally { setBrregBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/superadmin/tenants", Object.fromEntries(Object.entries(form).filter(([, v]) => v !== "")));
      toast.success("Kraftselskap opprettet");
      onCreated();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(", ") : d || "Feilet");
    }
    finally { setBusy(false); }
  };

  const field = (name, label, extra = {}) => (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{label}</label>
      <input value={form[name] ?? ""} onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm focus:outline-none focus:border-emerald-500/40"
        {...extra} data-testid={`new-tenant-${name}`} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-zinc-900 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Nytt kraftselskap</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Opprett en ny tenant og første admin-bruker. Bruk Brreg-oppslag for å prefylle selskapsinfo.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="border border-zinc-900 rounded-md p-4 bg-zinc-950/40">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium mb-2">Selskap</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Organisasjonsnummer *</label>
                <div className="flex gap-2">
                  <input value={form.orgnr} onChange={(e) => setForm({ ...form, orgnr: e.target.value.replace(/\D/g, "").slice(0, 9) })}
                    placeholder="9 sifre" required
                    data-testid="new-tenant-orgnr"
                    className="flex-1 h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm focus:outline-none focus:border-emerald-500/40 font-mono" />
                  <button type="button" onClick={lookupBrreg} disabled={brregBusy || form.orgnr.length !== 9}
                    data-testid="new-tenant-brreg"
                    className="h-10 px-3 rounded-md border border-emerald-500/30 text-emerald-400 text-xs hover:bg-emerald-500/10 disabled:opacity-40 flex items-center gap-1.5">
                    {brregBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                    Brreg
                  </button>
                </div>
              </div>
              {field("name", "Selskapsnavn *", { required: true })}
              {field("contact_email", "Kontakt e-post", { type: "email" })}
              {field("contact_phone", "Telefon")}
              {field("address", "Adresse")}
              <div className="grid grid-cols-2 gap-2">
                {field("postal_code", "Postnr")}
                {field("city", "Sted")}
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Standard prisområde</label>
                <select value={form.default_price_area}
                  data-testid="new-tenant-area"
                  onChange={(e) => setForm({ ...form, default_price_area: e.target.value })}
                  className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm">
                  {["NO1", "NO2", "NO3", "NO4", "NO5"].map((a) => <option key={a}>{a}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="border border-emerald-500/20 rounded-md p-4 bg-emerald-500/5">
            <div className="text-[11px] uppercase tracking-wider text-emerald-400 font-medium mb-2">Første admin-bruker</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {field("admin_full_name", "Fullt navn *", { required: true })}
              {field("admin_email", "E-post *", { type: "email", required: true })}
              <div className="md:col-span-2">
                {field("admin_password", "Passord (minst 10 tegn) *", { type: "password", required: true, minLength: 10 })}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-900">
            <button type="button" onClick={() => onOpenChange(false)}
              className="h-10 px-4 rounded-md border border-zinc-800 text-zinc-300 text-sm">Avbryt</button>
            <button type="submit" disabled={busy}
              data-testid="new-tenant-submit"
              className="h-10 px-5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium disabled:opacity-60 flex items-center gap-2">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Opprett kraftselskap
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
